import React, { useEffect } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { Chips } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Link, useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { IDictionary } from 'models/dictionary';
import { findInDictionary, parseDictionary } from 'helpers/dictionary';
import { LEGAL_ID } from 'pages/Firearms/RegisteredFirearmsMapPage/FirearmsLocationMap';
import { IAncillary, IComponent } from 'models/ancillaries';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { useLang } from 'models/langContext';
import { useDictionary } from 'models/dictionaryContext';
import { emptyFieldHolder } from 'components/molecules/CardList';

interface IComponentDataListItem {
  data: IComponent | IAncillary;
  state: IDictionary[] | null;
}

const ComponentDataListItem = ({ data, state }: IComponentDataListItem) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { hasPermissions } = usePermission();
  const isLegal = data.legalityUniqueId === LEGAL_ID;
  const { selectedLanguage } = useLang();
  const {
    getMake,
    make,
    getModel,
    model,
    getFirearmType,
    firearmType,
    getArtComponentType,
    artComponentType,
  } = useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getMake();
      getModel();
      getFirearmType();
      getArtComponentType();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: `${isLegal ? colors.gray100 : colors.error15}`,
      }}
    >
      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.componentDetails.section.serialNumber.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.serialNumber)}</Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.componentDetails.section.firecycleId.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.fireCycleId)}</Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.componentDetails.section.componentType.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(findInDictionary(data.typeUniqueId, artComponentType))}
        </Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.componentDetails.section.firearmType.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(findInDictionary(data.frtTypeUniqueId, firearmType))}
        </Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.componentDetails.section.make.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(findInDictionary(data.frtMakeUniqueId, make))}
        </Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.componentDetails.section.model.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(findInDictionary(data.frtModelUniqueId, model))}
        </Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.componentDetails.section.state.label')}
        </Typography>
        <Chips
          text={findInDictionary(data.stateUniqueId, state)}
          variant={
            renderDataColorByValues(
              data.activityUniqueId,
              data.legalityUniqueId,
              data.stateUniqueId,
            ).stateField
          }
        />
      </Grid>

      {hasPermissions(
        [EPermission.VIEW_LIMITED_ANCILLARIES, EPermission.VIEW_ALL_ANCILLARIES],
        true,
      ) && (
        <Grid
          size={{ xs: 12 }}
          sx={{
            margin: '0 10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: {
              xs: `1px solid ${colors.gray200}`,
            },
            p: 2,
          }}
        >
          <Grid size={{ xs: 12 }}>
            <Link
              to={ERouteLinks.ViewComponents.replace(':id', data.uniqueId)}
              target="_blank"
            >
              <Button
                label={t('registeredFirearms.firearmsLocation.seeDetails.label')}
                variant={EButtonVariants.outlined}
                size="small"
                fullWidth
              />
            </Link>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default ComponentDataListItem;
